import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorTheme } from '@figma/fpl-themes';
import fplTokens from '@figma/fpl-tokens/css-vars';
import { Trans } from '@/UI/Trans/Trans';
import { LabelSmRg, TextLgMd, TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { Box } from '@/UI/Box/Box';
import { FloatingPanel, type FloatingPanelProps } from '@/UI/FloatingPanel/FloatingPanel';
import { AppPaper, Flex, FlexCenHorVer, FlexCenVerSpaceBetween, FlexCol } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';
import { TourStepMediaType } from '@/enums/tour-step-media-type.enum';
import { AllowWheelEvents } from '@/components/Common/AllowWheelEvents';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { color } from '@/colors';
import { TourKeys } from '../tour-keys';
import { tourConfigs, useTour } from '../TourContext';
import { LottieAnimation } from '../../Common/LottieAnimation';
import type { TourAction, TourStepMedia } from '../tourTypes';
import type { SxProps, Theme } from '@/UI/theme.exports';

const MEDIA_HEIGHT = 100;
const MEDIA_WIDTH = 80;

const TourMedia = ({ media }: { media: TourStepMedia[keyof TourStepMedia] | null }) => {
  if (!media) return null;
  if (
    media.type !== TourStepMediaType.IMAGE &&
    media.type !== TourStepMediaType.SVG &&
    media.type !== TourStepMediaType.LOTTIE
  ) {
    return null;
  }
  return (
    <Flex>
      {media.type === TourStepMediaType.IMAGE || media.type === TourStepMediaType.SVG ? (
        <img src={media.path} style={{ width: MEDIA_WIDTH, height: MEDIA_HEIGHT }} />
      ) : media.type === TourStepMediaType.LOTTIE ? (
        <LottieAnimation src={media.path} width={MEDIA_WIDTH} height={MEDIA_HEIGHT} fallback={media.fallback} />
      ) : null}
    </Flex>
  );
};

interface TooltipAction {
  label: string;
  onClick: () => void;
  mode?: 'filled-light' | 'text' | 'outlined-light';
  disabled?: boolean;
}

const HORIZONTAL_OFFSET = 'calc(50% - 400px)';
const Y_TRANSLATE = 150;
const VERTICAL_OFFSET = `calc(50% - ${Y_TRANSLATE}px)`;
const VERTICAL_OFFSET_FPL_DARK = '50%';
const TOUR_TOOLTIP_WIDTH = 800;
const TOUR_TOOLTIP_WIDTH_FPL_DARK = 240;

const floatingPanelSx = {
  border: 'none',
  gap: 0,
  padding: 0,
};

const paperSx: SxProps<Theme> = {
  padding: 0,
  position: 'relative',
  border: `1px solid ${color.Weavy_Menu_Border}`,
  width: `${TOUR_TOOLTIP_WIDTH}px`,
  maxWidth: `${TOUR_TOOLTIP_WIDTH}px`,
  userSelect: 'none',
};

const paperSxFplDark: SxProps<Theme> = {
  padding: 0,
  position: 'relative',
  bgcolor: fplTokens.color.bg,
  color: fplTokens.color.text,
  border: 'none',
  borderRadius: fplTokens.radius.large,
  boxShadow: fplTokens.elevation.x500,
  width: `${TOUR_TOOLTIP_WIDTH_FPL_DARK}px`,
  maxWidth: `${TOUR_TOOLTIP_WIDTH_FPL_DARK}px`,
  userSelect: 'none',
};

// Amount of steps that are not relevant to the tour (Welcome / Finish Tour)
const NON_RELEVANT_STEPS_AMOUNT = {
  [TourKeys.NavigationTour]: 2,
  [TourKeys.GetToKnowTheInterface]: 2,
  [TourKeys.LearnToShare]: 2,
  [TourKeys.SaveNodes]: 0,
  [TourKeys.NUXEmptyCanvas]: 0,
};

interface TourTooltipFplDarkBodyProps {
  actions: TooltipAction[];
  activeTour: TourKeys | null;
  content: string;
  currentStep: number;
  handleEndTour: () => void;
  isLoading: boolean;
  showCloseButton: boolean;
  title: string;
  tourContext: string | undefined;
}

const TourTooltipFplDarkBody = ({
  actions,
  activeTour,
  content,
  currentStep,
  handleEndTour,
  isLoading,
  showCloseButton,
  title,
  tourContext,
}: TourTooltipFplDarkBodyProps) => {
  const { t } = useTranslation();
  return (
    <>
      {!showCloseButton ? null : (
        <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
          <AppXBtn sx={{ color: fplTokens.color.text }} size={16} onClick={handleEndTour} />
        </Box>
      )}
      <FlexCol sx={{ gap: 1 }}>
        <TextSmMd sx={{ color: fplTokens.color.text, pr: 4, pt: 1.5, pb: 0.5, pl: 2, fontWeight: 500 }}>{title}</TextSmMd>
        <Box sx={{ height: '1px', bgcolor: fplTokens.color.border, width: '100%' }} />
        <TextSmRg sx={{ whiteSpace: 'pre-wrap', color: fplTokens.color.textSecondary, pr: 2, pb: 0, pl: 2 }}>
          <Trans t={t} i18nKey={content} context={tourContext} />
        </TextSmRg>
        <FlexCenVerSpaceBetween sx={{ flexDirection: 'row', gap: 2, pt: 1 }}>
          <Flex sx={{ pl: 2, pb: 1 }}>
            {activeTour ? (
              <TextSmMd sx={{ color: fplTokens.color.text }}>
                {t(I18N_KEYS.PRODUCT_TOURS.TOUR_STEP_INDICATION, {
                  step: currentStep + 1 - NON_RELEVANT_STEPS_AMOUNT[activeTour],
                  total: tourConfigs[activeTour].steps.length - NON_RELEVANT_STEPS_AMOUNT[activeTour],
                })}
              </TextSmMd>
            ) : null}
          </Flex>
          <Flex sx={{ gap: 1, pb: 1, pr: 1, minWidth: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {actions.map((action) => (
              <ButtonContained
                key={action.label}
                loading={isLoading}
                mode={action.mode}
                onClick={action.onClick}
                disabled={action.disabled}
                sx={{
                  minWidth: 0,
                  px: '8px',
                  ...(action.mode === 'text' ? { color: fplTokens.color.text } : null),
                }}
                size="xs"
              >
                {action.label}
              </ButtonContained>
            ))}
          </Flex>
        </FlexCenVerSpaceBetween>
      </FlexCol>
    </>
  );
};

const getPopperPosition = (placement: 'top' | 'bottom' | 'left' | 'right', isFplDark: boolean) => {
  if (placement === 'top') {
    return {
      top: '100px',
      left: HORIZONTAL_OFFSET,
      bottom: 'auto',
    };
  } else if (placement === 'bottom') {
    return {
      bottom: '75px',
      left: HORIZONTAL_OFFSET,
      top: 'auto',
    };
  } else if (placement === 'left') {
    return {
      top: isFplDark ? VERTICAL_OFFSET_FPL_DARK : VERTICAL_OFFSET,
      left: '100px',
      right: 'auto',
    };
  } else if (placement === 'right') {
    return {
      top: isFplDark ? VERTICAL_OFFSET_FPL_DARK : VERTICAL_OFFSET,
      left: 'auto',
      right: '100px',
    };
  }
};

// Skidding moves along the anchor; offset moves away from it in the placement direction.
const getPositionAdjustmentTransform = (placement: 'top' | 'bottom' | 'left' | 'right', skidding = 0, offset = 0) => {
  // Leave the base positioning unchanged when no adjustment is configured.
  if (!skidding && !offset) return undefined;

  switch (placement) {
    case 'left':
      return `translate(${-offset}px, ${skidding}px)`;
    case 'right':
      return `translate(${offset}px, ${skidding}px)`;
    case 'top':
      return `translate(${skidding}px, ${-offset}px)`;
    case 'bottom':
      return `translate(${skidding}px, ${offset}px)`;
  }
};

interface TourTooltipProps {
  actions?: TourAction[];
  anchorEl?: HTMLElement | null;
  content: string;
  media?: TourStepMedia;
  parentId?: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
  skidding?: number;
  offset?: number;
  title: string;
}

export const TourTooltip = ({
  actions: propActions,
  anchorEl,
  content,
  media,
  placement,
  skidding,
  offset,
  title,
}: TourTooltipProps) => {
  const {
    activeTour,
    allowBack,
    callAction,
    canMoveToNextStep,
    context,
    currentStep,
    endTour,
    getCurrentStepConfig,
    goToNextStep,
    goToPreviousStep,
  } = useTour();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [resolvedPlacement, setResolvedPlacement] = useState(placement);
  const handlePlacementChange = useCallback((nextPlacement: NonNullable<FloatingPanelProps['placement']>) => {
    // need to change the arrow's side when the tooltip placement changes
    const side = nextPlacement.split('-')[0];
    if (side === 'top' || side === 'bottom' || side === 'left' || side === 'right') {
      setResolvedPlacement(side);
    }
  }, []);
  const tourContext = useMemo(() => {
    if (!activeTour) return '';
    if (!context) return '';
    return context[activeTour];
  }, [context, activeTour]);

  const runAction = useCallback(async (fn: () => Promise<void>) => {
    setIsLoading(true);
    await fn();
    setIsLoading(false);
  }, []);

  const resolvedMedia = useMemo(() => {
    if (!media) return null;
    if (tourContext && Object.keys(media).includes(tourContext)) {
      return media[tourContext];
    }
    return media.default;
  }, [media, tourContext]);

  const variant = (activeTour && tourConfigs[activeTour]?.variant) || 'fpl-dark';
  const isFplDark = variant === 'fpl-dark';
  const isAnnouncement = Boolean(activeTour && tourConfigs[activeTour]?.kind === 'announcement');
  const primaryMode: TooltipAction['mode'] = 'filled-light';

  const actions = useMemo<TooltipAction[]>(() => {
    if (propActions) {
      return propActions.map((action) => ({
        label: t(action.label),
        onClick: () => void runAction(() => callAction(action)),
        mode: action.variant === 'primary' ? primaryMode : 'text',
      }));
    }

    const currentStepConfig = getCurrentStepConfig();
    if (!currentStepConfig) return [];

    const backButton: TooltipAction = {
      label: t(I18N_KEYS.PRODUCT_TOURS.PREVIOUS_STEP),
      onClick: goToPreviousStep,
      mode: 'text',
    };
    const nextButton: TooltipAction = {
      label: t(I18N_KEYS.PRODUCT_TOURS.NEXT_STEP),
      onClick: () => void runAction(goToNextStep),
      mode: primaryMode,
      disabled: !canMoveToNextStep,
    };
    return [...(allowBack ? [backButton] : []), ...(currentStepConfig.autoPass ? [] : [nextButton])];
  }, [
    allowBack,
    callAction,
    canMoveToNextStep,
    getCurrentStepConfig,
    goToNextStep,
    goToPreviousStep,
    primaryMode,
    propActions,
    runAction,
    t,
  ]);

  const isLastStep = useMemo(() => {
    if (!activeTour) return false;
    return currentStep === tourConfigs[activeTour].steps.length - 1;
  }, [activeTour, currentStep]);

  // An announcement is always on its last step but still needs its X.
  const showCloseButton = isAnnouncement || !isLastStep;

  const tooltipBody = isFplDark ? (
    <TourTooltipFplDarkBody
      actions={actions}
      activeTour={activeTour}
      content={content}
      currentStep={currentStep}
      handleEndTour={endTour}
      isLoading={isLoading}
      showCloseButton={showCloseButton}
      title={title}
      tourContext={tourContext}
    />
  ) : (
    <>
      {!showCloseButton ? null : <AppXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={endTour} />}
      <FlexCenHorVer
        sx={{
          gap: 4,
          pt: 3,
          pb: 2,
          pr: 2,
          pl: 3,
        }}
      >
        <TourMedia media={resolvedMedia} />
        <FlexCol
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            minHeight: MEDIA_HEIGHT,
          }}
        >
          <FlexCol sx={{ gap: 1, width: '90%' }}>
            <LabelSmRg sx={{ opacity: 0.8 }}>{title}</LabelSmRg>
            <TextLgMd sx={{ whiteSpace: 'pre-wrap', lineHeight: '1.3' }}>
              <Trans t={t} i18nKey={content} context={tourContext} />
            </TextLgMd>
          </FlexCol>
          <FlexCenVerSpaceBetween
            sx={{
              flexDirection: 'row',
              gap: 2,
            }}
          >
            <Flex>
              {activeTour ? (
                <TextSmRg sx={{ opacity: 0.4 }}>
                  {t(I18N_KEYS.PRODUCT_TOURS.TOUR_STEP_INDICATION, {
                    step: currentStep + 1 - NON_RELEVANT_STEPS_AMOUNT[activeTour],
                    total: tourConfigs[activeTour].steps.length - NON_RELEVANT_STEPS_AMOUNT[activeTour],
                  })}
                </TextSmRg>
              ) : null}
            </Flex>
            <Flex sx={{ gap: 2 }}>
              {actions.map((action) => (
                <ButtonContained
                  key={action.label}
                  loading={isLoading}
                  mode={action.mode}
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  {action.label}
                </ButtonContained>
              ))}
            </Flex>
          </FlexCenVerSpaceBetween>
        </FlexCol>
      </FlexCenHorVer>
    </>
  );

  const resolvedPaperSx = isFplDark ? paperSxFplDark : paperSx;
  const fplDarkClass = isFplDark ? 'tour-tooltip-fpl-dark' : undefined;
  const popperSkidding = isFplDark ? 35 : 0;
  const popperOffset = isFplDark ? 24 : 10;
  const positionAdjustmentTransform = getPositionAdjustmentTransform(
    anchorEl ? resolvedPlacement : placement,
    skidding,
    offset,
  );
  const adjustedFloatingPanelSx = positionAdjustmentTransform
    ? { ...floatingPanelSx, transform: positionAdjustmentTransform }
    : floatingPanelSx;

  if (anchorEl) {
    return (
      <FloatingPanel
        open
        anchorEl={anchorEl}
        fplPopoverType="tutorial"
        placement={placement}
        onPlacementChange={handlePlacementChange}
        skidding={popperSkidding}
        offset={popperOffset}
        sx={adjustedFloatingPanelSx}
      >
        <AllowWheelEvents targetElementId="react-flow-background">
          <ColorTheme brand="weave" mode="dark">
            <AppPaper className={fplDarkClass} data-placement={resolvedPlacement} sx={resolvedPaperSx}>
              {tooltipBody}
            </AppPaper>
          </ColorTheme>
        </AllowWheelEvents>
      </FloatingPanel>
    );
  }

  return (
    <AllowWheelEvents targetElementId="react-flow-background">
      <ColorTheme brand="weave" mode="dark">
        <AppPaper
          className={fplDarkClass}
          data-placement={placement}
          sx={{
            ...resolvedPaperSx,
            position: 'fixed',
            ...getPopperPosition(placement, isFplDark),
            transform: positionAdjustmentTransform,
          }}
        >
          {tooltipBody}
        </AppPaper>
      </ColorTheme>
    </AllowWheelEvents>
  );
};
